import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FaBook, FaBalanceScale, FaChevronRight, FaChevronLeft, FaTimes, FaUpload } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import ArtHistoryAppreciationScroller from './ArtHistoryAppreciationScroller';
import ArtComparisonScroller from './ArtComparisonScroller';
import ArtAnalysisHotspots from './ArtAnalysisHotspots';
import ArtActivitySection from './ArtActivitySection';
import { backendConfig } from '../config/backendConfig';

const ArtHistoryCard = ({ artworkGroup, onDeleteActivity }) => {
  const navigate = useNavigate();
  const API_BASE = backendConfig.baseUrl;
  const [activeSubCard, setActiveSubCard] = useState(null); // null, 'appreciation', or 'comparison'
  const [showAppreciationModal, setShowAppreciationModal] = useState(false);
  const [selectedAppreciationActivity, setSelectedAppreciationActivity] = useState(null);
  const [appreciationModalData, setAppreciationModalData] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [selectedComparisonActivity, setSelectedComparisonActivity] = useState(null);
  const [comparisonModalData, setComparisonModalData] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isActivityPopupOpen, setIsActivityPopupOpen] = useState(false);
  const [activeSimilarityIndex, setActiveSimilarityIndex] = useState(null);
  const [activeDifferenceIndex, setActiveDifferenceIndex] = useState(null);
  const [isInsightExpanded, setIsInsightExpanded] = useState(false);
  
  // Upload state for appreciation activities
  const [activityFile, setActivityFile] = useState(null);
  const [showShareConfirmation, setShowShareConfirmation] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [uploadSuccessData, setUploadSuccessData] = useState(null);
  const { user } = useAuth();

  // State to store full activity data with images
  const [fullAppreciations, setFullAppreciations] = useState([]);
  const [fullComparisons, setFullComparisons] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);

  // Separate activities by type
  const { appreciations, comparisons } = useMemo(() => {
    const app = [];
    const comp = [];
    
    artworkGroup.activities?.forEach(activity => {
      // Check both type and feature for compatibility
      if (activity.type === 'art-history-appreciation' || activity.feature === 'art-history-appreciation' || activity.metadata?.feature === 'art-history-appreciation') {
        app.push(activity);
      } else if (activity.type === 'art-comparison' || activity.feature === 'art-comparison' || activity.metadata?.feature === 'art-comparison') {
        comp.push(activity);
      }
    });

    // Sort by creation date (newest first)
    app.sort((a, b) => {
      let aDate, bDate;
      try {
        if (a.createdAt?.toDate) {
          aDate = a.createdAt.toDate();
        } else if (a.createdAt?._seconds) {
          aDate = new Date(a.createdAt._seconds * 1000);
        } else {
          aDate = new Date(a.createdAt);
        }
        if (b.createdAt?.toDate) {
          bDate = b.createdAt.toDate();
        } else if (b.createdAt?._seconds) {
          bDate = new Date(b.createdAt._seconds * 1000);
        } else {
          bDate = new Date(b.createdAt);
        }
        
        // Handle invalid dates - put them at the end
        if (isNaN(aDate.getTime())) return 1;
        if (isNaN(bDate.getTime())) return -1;
        
        return bDate - aDate;
      } catch (error) {
        return 0;
      }
    });

    comp.sort((a, b) => {
      let aDate, bDate;
      try {
        if (a.createdAt?.toDate) {
          aDate = a.createdAt.toDate();
        } else if (a.createdAt?._seconds) {
          aDate = new Date(a.createdAt._seconds * 1000);
        } else {
          aDate = new Date(a.createdAt);
        }
        if (b.createdAt?.toDate) {
          bDate = b.createdAt.toDate();
        } else if (b.createdAt?._seconds) {
          bDate = new Date(b.createdAt._seconds * 1000);
        } else {
          bDate = new Date(b.createdAt);
        }
        
        // Handle invalid dates - put them at the end
        if (isNaN(aDate.getTime())) return 1;
        if (isNaN(bDate.getTime())) return -1;
        
        return bDate - aDate;
      } catch (error) {
        return 0;
      }
    });

    return { appreciations: app, comparisons: comp };
  }, [artworkGroup.activities]);

  // Load full activity data with images
  useEffect(() => {
    const loadFullActivities = async () => {
      if (appreciations.length === 0 && comparisons.length === 0) {
        setLoadingActivities(false);
        return;
      }

      setLoadingActivities(true);
      try {
        // Load full appreciation data
        const loadedAppreciations = await Promise.all(
          appreciations.map(async (activity) => {
            try {
              const docRef = doc(db, 'userActivities', activity.id);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() };
              }
              return activity;
            } catch (error) {
              console.error('Error loading appreciation activity:', error);
              return activity;
            }
          })
        );

        // Load full comparison data
        const loadedComparisons = await Promise.all(
          comparisons.map(async (activity) => {
            try {
              const docRef = doc(db, 'userActivities', activity.id);
              const docSnap = await getDoc(docRef);
              if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() };
              }
              return activity;
            } catch (error) {
              console.error('Error loading comparison activity:', error);
              return activity;
            }
          })
        );

        setFullAppreciations(loadedAppreciations);
        setFullComparisons(loadedComparisons);
      } catch (error) {
        console.error('Error loading activities:', error);
        setFullAppreciations(appreciations);
        setFullComparisons(comparisons);
      } finally {
        setLoadingActivities(false);
      }
    };

    loadFullActivities();
  }, [appreciations, comparisons]);

  const handleSubCardClick = (type) => {
    if (activeSubCard === type) {
      setActiveSubCard(null); // Close if already open
    } else {
      setActiveSubCard(type); // Open the selected sub-card
    }
  };

  // Handle activity artwork upload
  const handleUploadActivity = async (shareToCoLearning = false) => {
    if (!activityFile || !selectedAppreciationActivity || !appreciationModalData) {
      alert('Please select a file to upload');
      return;
    }

    if (!user) {
      alert('Please log in to upload artwork');
      return;
    }

    setIsUploading(true);
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 60000); // 60 second timeout

    try {
      const artworkMeta = appreciationModalData.artworkMeta || {};
      // Use artworkId (imageUrl) as the key, same as backend expects
      const artworkKey = artworkMeta.imageUrl || selectedAppreciationActivity.originalImageUrl || artworkMeta.title || 'unknown';
      
      const formData = new FormData();
      formData.append('file', activityFile);
      formData.append('public', shareToCoLearning.toString());
      formData.append('uid', user.uid);
      formData.append('userName', user.displayName || user.email || 'Anonymous');
      formData.append('activityType', 'art-history-appreciation');
      formData.append('artworkId', artworkKey);
      formData.append('artworkTitle', artworkMeta.title || 'Unknown Artwork');
      formData.append('artworkArtist', artworkMeta.artist || 'Unknown Artist');
      formData.append('artworkMovement', artworkMeta.movement || '');
      formData.append('artworkImageUrl', artworkMeta.imageUrl || selectedAppreciationActivity.originalImageUrl || '');

      console.log('📤 Uploading art history activity:', {
        artworkKey,
        artworkTitle: artworkMeta.title,
        fileSize: activityFile.size,
        shareToCoLearning
      });

      const uploadEndpoint = API_BASE.endsWith('/api')
        ? `${API_BASE}/upload-art-history-activity`
        : `${API_BASE}/api/upload-art-history-activity`;
      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: formData,
        signal: abortController.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const result = await response.json();
        setUploadSuccessData({
          shared: shareToCoLearning,
          message: shareToCoLearning 
            ? 'Artwork uploaded and shared to Co-Learning Space and Artist Wall!' 
            : 'Artwork uploaded successfully to Artist Wall!'
        });
        setActivityFile(null);
        setShowSuccessPopup(true);
        // Close the appreciation modal
        setShowAppreciationModal(false);
        setSelectedAppreciationActivity(null);
        setAppreciationModalData(null);
        setIsExpanded(false);
      } else {
        if (response.status === 404) {
          alert('Upload service is temporarily unavailable. Please try again later or contact support.');
          return;
        }
        const errorText = await response.text();
        let error;
        try {
          error = JSON.parse(errorText);
        } catch {
          error = { error: errorText || 'Failed to upload activity' };
        }
        alert(error.error || 'Failed to upload activity');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.error('❌ Upload timeout after 60 seconds');
        alert('Upload timed out. Please try again with a smaller file or check your connection.');
      } else {
        console.error('❌ Error uploading activity:', error);
        alert(`Error uploading activity: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsUploading(false);
    }
  };


  const handleAppreciationDoubleClick = async (activity) => {
    try {
      // Load full activity data
      const docRef = doc(db, 'userActivities', activity.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const fullActivity = { id: docSnap.id, ...docSnap.data() };
        setSelectedAppreciationActivity(fullActivity);
        
        // Prepare modal data similar to ArtHistoryAppreciation
        const artworkMeta = fullActivity.metadata?.artworkMeta || {};
        const appreciation = fullActivity.appreciation || [];
        const points = fullActivity.points || [];
        
        setAppreciationModalData({
          appreciation: appreciation.length > 0 ? appreciation : points,
          concepts: fullActivity.concepts || [],
          activity: fullActivity.activity || null,
          artworkMeta: artworkMeta
        });
        
        setShowAppreciationModal(true);
      }
    } catch (error) {
      console.error('Error loading appreciation activity:', error);
    }
  };

  const handleComparisonDoubleClick = async (activity) => {
    try {
      // Load full activity data
      const docRef = doc(db, 'userActivities', activity.id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const fullActivity = { id: docSnap.id, ...docSnap.data() };
        setSelectedComparisonActivity(fullActivity);
        
        // Prepare modal data for comparison
        // Check both top-level and metadata for artwork and comparison data
        const metadata = fullActivity.metadata || {};
        
        // Get artwork1 - check top-level first, then metadata
        const artwork1 = fullActivity.artwork1 || metadata.artwork1 || {};
        // Get artwork2 - check top-level first, then metadata
        const artwork2 = fullActivity.artwork2 || metadata.artwork2 || {};
        
        // Get comparison result - check top-level first, then metadata
        const comparisonResult = fullActivity.comparisonResult || metadata.comparisonResult || {};
        
        // Also check for activity in comparison result
        const activityData = comparisonResult.activity || fullActivity.activity || null;
        
        console.log('Comparison modal data:', {
          artwork1,
          artwork2,
          comparisonResult,
          hasSimilarities: !!(comparisonResult.similarities?.length),
          hasDifferences: !!(comparisonResult.differences?.length),
          hasInsight: !!comparisonResult.insight,
          hasActivity: !!activityData
        });
        
        setComparisonModalData({
          comparisonResult: {
            ...comparisonResult,
            activity: activityData
          },
          artwork1: {
            ...artwork1,
            // Ensure imageUrl is available
            imageUrl: artwork1.imageUrl || fullActivity.originalImageUrl || '',
            // Ensure artistName is available
            artistName: artwork1.artistName || artwork1.artist || 'Unknown Artist',
            // Ensure title is available
            title: artwork1.title || 'Artwork 1',
            // Ensure artMovement is available
            artMovement: artwork1.artMovement || artwork1.movement || ''
          },
          artwork2: {
            ...artwork2,
            // Ensure imageUrl is available
            imageUrl: artwork2.imageUrl || fullActivity.transformedImageUrl || '',
            // Ensure artistName is available
            artistName: artwork2.artistName || artwork2.artist || 'Unknown Artist',
            // Ensure title is available
            title: artwork2.title || 'Artwork 2',
            // Ensure artMovement is available
            artMovement: artwork2.artMovement || artwork2.movement || ''
          },
          title: fullActivity.title || 'Art Comparison'
        });
        
        setShowComparisonModal(true);
      }
    } catch (error) {
      console.error('Error loading comparison activity:', error);
    }
  };

  return (
    <div className="bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden relative border-2 border-white/30">
      {/* Main Header */}
      <div className="absolute -top-3 left-0 right-0 bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 text-white py-2 text-center text-sm font-bold shadow-lg z-20 border-2 border-amber-300">
        Art History
      </div>

      <div className="p-6 pt-10">
        {/* Sub-cards */}
        <div className="flex flex-col gap-4 mb-4">
          {/* Art History Appreciation Sub-card - Compact Style with Scroller */}
          {fullAppreciations.length > 0 && (
            <div className="w-full bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl overflow-hidden border-2 border-orange-200 shadow-md relative">
              {/* Header Tag */}
              <div className="absolute top-2 left-2 z-10 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                Art History Appreciation
              </div>
              
              {/* Horizontal Scroller for All Appreciations */}
              {loadingActivities ? (
                <div className="pt-10 pb-4 px-4 text-center">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                </div>
              ) : (
                <div
                  className="w-full pt-10 pb-4 px-4 overflow-x-auto"
                  style={{ scrollbarWidth: 'thin', scrollbarColor: '#f97316 transparent' }}
                >
                  <div className="flex gap-4 px-1 w-full">
                    {fullAppreciations.map((appreciation, index) => {
                      const artworkMeta = appreciation.metadata?.artworkMeta || {};
                      // Try multiple possible image URL locations
                      const imageUrl = appreciation.originalImageUrl || 
                                      appreciation.metadata?.originalImageUrl ||
                                      appreciation.metadata?.artworkMeta?.imageUrl ||
                                      artworkMeta.imageUrl ||
                                      artworkMeta.originalImageUrl;
                      
                      console.log('Appreciation image check:', {
                        id: appreciation.id,
                        originalImageUrl: appreciation.originalImageUrl,
                        metadataOriginal: appreciation.metadata?.originalImageUrl,
                        artworkMetaImage: artworkMeta.imageUrl,
                        finalImageUrl: imageUrl
                      });
                    
                      return (
                        <motion.div
                          key={appreciation.id || index}
                          className="flex-shrink-0 w-[270px] bg-white rounded-lg overflow-hidden border-2 border-orange-200 shadow-sm cursor-pointer hover:shadow-lg transition-all relative"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAppreciationDoubleClick(appreciation);
                          }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {/* Image Thumbnail Area */}
                          <div className="relative w-full h-48 border-b-2 border-orange-200 overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50">
                            {imageUrl ? (
                              <>
                                <img
                                  src={imageUrl}
                                  alt={artworkMeta.title || 'Artwork'}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const fallback = e.target.parentElement.querySelector('.image-fallback');
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                />
                                {/* Fallback placeholder if image fails to load */}
                                <div className="image-fallback hidden absolute inset-0 items-center justify-center">
                                  <div className="text-center">
                                    <FaBook className="w-12 h-12 text-amber-300 mx-auto mb-2" />
                                    <p className="text-xs text-amber-600 font-medium">Artwork</p>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                  <FaBook className="w-12 h-12 text-amber-300 mx-auto mb-2" />
                                  <p className="text-xs text-amber-600 font-medium">No Image</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Style Tag Overlay */}
                            {artworkMeta.movement && (
                              <div className="absolute bottom-2 left-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-md z-10">
                                {artworkMeta.movement}
                              </div>
                            )}
                            
                            {/* Title Overlay (if space allows) */}
                            {artworkMeta.title && (
                              <div className="absolute top-2 left-2 right-2">
                                <div className="bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded text-xs font-medium truncate">
                                  {artworkMeta.title}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Artwork Info */}
                          <div className="p-2">
                            {/* Title */}
                            {artworkMeta.title && (
                              <h4 className="text-sm font-semibold text-gray-800 truncate mb-1">
                                {artworkMeta.title}
                              </h4>
                            )}
                            
                            {/* Artist */}
                            {artworkMeta.artist && (
                              <p className="text-xs text-gray-600 truncate mb-2">
                                {artworkMeta.artist}
                              </p>
                            )}
                            
                            {/* Date */}
                            {appreciation.createdAt && (() => {
                              let date;
                              try {
                                if (appreciation.createdAt?.toDate) {
                                  date = appreciation.createdAt.toDate();
                                } else if (appreciation.createdAt?._seconds) {
                                  date = new Date(appreciation.createdAt._seconds * 1000);
                                } else if (typeof appreciation.createdAt === 'number') {
                                  date = new Date(appreciation.createdAt);
                                } else if (typeof appreciation.createdAt === 'string') {
                                  date = new Date(appreciation.createdAt);
                                } else {
                                  date = new Date(appreciation.createdAt);
                                }
                                
                                // Check if date is valid
                                if (isNaN(date.getTime())) {
                                  return null;
                                }
                                
                                return (
                                  <div className="text-center py-1 text-xs text-amber-800 font-medium">
                                    {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </div>
                                );
                              } catch (error) {
                                console.error('Error formatting date:', error);
                                return null;
                              }
                            })()}
                          </div>
                          
                          {/* View Details Button */}
                          <div className="px-3 pb-3">
                            <div className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white text-center py-1.5 rounded-lg font-semibold text-xs hover:from-orange-600 hover:to-yellow-600 transition-all">
                              View details
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Art of Comparison Sub-card - Compact Style with Scroller */}
          {fullComparisons.length > 0 && (
            <div className="w-full bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl overflow-hidden border-2 border-orange-200 shadow-md relative">
              {/* Header Tag */}
              <div className="absolute top-2 left-2 z-10 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md">
                Art of Comparison
              </div>
              
              {/* Horizontal Scroller for All Comparisons */}
              {loadingActivities ? (
                <div className="pt-10 pb-4 px-4 text-center">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                </div>
              ) : (
                <div
                  className="w-full pt-10 pb-4 px-4 overflow-x-auto"
                  style={{ scrollbarWidth: 'thin', scrollbarColor: '#f97316 transparent' }}
                >
                  <div className="flex gap-4 px-1 w-full">
                    {fullComparisons.map((comparison, index) => {
                      // Extract artwork data - comparison has artwork1 and artwork2
                      const artwork1 = comparison.artwork1 || {};
                      const artwork2 = comparison.artwork2 || {};
                      
                      // Use first artwork's image for thumbnail
                      const imageUrl = artwork1.imageUrl || 
                                      artwork1.originalImageUrl ||
                                      comparison.originalImageUrl ||
                                      artwork2.imageUrl ||
                                      artwork2.originalImageUrl;
                      
                      const artwork1Title = artwork1.title || 'Artwork 1';
                      const artwork2Title = artwork2.title || 'Artwork 2';
                      const displayTitle = `${artwork1Title} & ${artwork2Title}`;
                      const artwork1Artist = artwork1.artistName || artwork1.artist || 'Unknown';
                      const artwork2Artist = artwork2.artistName || artwork2.artist || 'Unknown';
                      
                      return (
                        <motion.div
                          key={comparison.id || index}
                          className="flex-shrink-0 w-[270px] bg-white rounded-lg overflow-hidden border-2 border-orange-200 shadow-sm cursor-pointer hover:shadow-lg transition-all relative"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleComparisonDoubleClick(comparison);
                          }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {/* Image Thumbnail Area */}
                          <div className="relative w-full h-48 border-b-2 border-orange-200 overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50">
                            {imageUrl ? (
                              <>
                                <img
                                  src={imageUrl}
                                  alt={displayTitle}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    const fallback = e.target.parentElement.querySelector('.image-fallback');
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                />
                                {/* Fallback placeholder if image fails to load */}
                                <div className="image-fallback hidden absolute inset-0 items-center justify-center">
                                  <div className="text-center">
                                    <FaBalanceScale className="w-12 h-12 text-amber-300 mx-auto mb-2" />
                                    <p className="text-xs text-amber-600 font-medium">Comparison</p>
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-center">
                                  <FaBalanceScale className="w-12 h-12 text-amber-300 mx-auto mb-2" />
                                  <p className="text-xs text-amber-600 font-medium">No Image</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Comparison Tag Overlay */}
                            <div className="absolute bottom-2 left-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-md z-10">
                              Comparison
                            </div>
                            
                            {/* Title Overlay */}
                            {displayTitle && (
                              <div className="absolute top-2 left-2 right-2">
                                <div className="bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded text-xs font-medium truncate">
                                  {displayTitle}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Artwork Info */}
                          <div className="p-2">
                            {/* Title */}
                            <h4 className="text-sm font-semibold text-gray-800 truncate mb-1">
                              {displayTitle}
                            </h4>
                            
                            {/* Artists */}
                            <p className="text-xs text-gray-600 truncate mb-1">
                              {artwork1Artist} & {artwork2Artist}
                            </p>
                            
                            {/* Date */}
                            {comparison.createdAt && (() => {
                              let date;
                              try {
                                if (comparison.createdAt?.toDate) {
                                  date = comparison.createdAt.toDate();
                                } else if (comparison.createdAt?._seconds) {
                                  date = new Date(comparison.createdAt._seconds * 1000);
                                } else if (typeof comparison.createdAt === 'number') {
                                  date = new Date(comparison.createdAt);
                                } else if (typeof comparison.createdAt === 'string') {
                                  date = new Date(comparison.createdAt);
                                } else {
                                  date = new Date(comparison.createdAt);
                                }
                                
                                // Check if date is valid
                                if (isNaN(date.getTime())) {
                                  return null;
                                }
                                
                                return (
                                  <div className="text-center py-1 text-xs text-amber-800 font-medium">
                                    {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  </div>
                                );
                              } catch (error) {
                                console.error('Error formatting date:', error);
                                return null;
                              }
                            })()}
                          </div>
                          
                          {/* View Details Button */}
                          <div className="px-3 pb-3">
                            <div className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white text-center py-1.5 rounded-lg font-semibold text-xs hover:from-orange-600 hover:to-yellow-600 transition-all">
                              View details
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scroller Views */}
        <AnimatePresence mode="wait">
          {activeSubCard === 'appreciation' && appreciations.length > 0 && (
            <motion.div
              key="appreciation-scroller"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ArtHistoryAppreciationScroller
                activities={appreciations}
                onActivityClick={(activity) => {
                  // Navigate to art history appreciation page with the activity data
                  navigate('/art-history-appreciation', {
                    state: {
                      activityId: activity.id,
                      fromDashboard: true
                    }
                  });
                }}
                onActivityDoubleClick={handleAppreciationDoubleClick}
                onDeleteActivity={onDeleteActivity}
              />
            </motion.div>
          )}

          {activeSubCard === 'comparison' && comparisons.length > 0 && (
            <motion.div
              key="comparison-scroller"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ArtComparisonScroller
                activities={comparisons}
                onActivityClick={(activity) => {
                  // Navigate to art comparison page with the activity data
                  navigate('/art-history-compare', {
                    state: {
                      activityId: activity.id,
                      fromDashboard: true
                    }
                  });
                }}
                onDeleteActivity={onDeleteActivity}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty states */}
        {activeSubCard === 'appreciation' && appreciations.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <FaBook className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No art history appreciations yet</p>
          </div>
        )}

        {activeSubCard === 'comparison' && comparisons.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <FaBalanceScale className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No art comparisons yet</p>
          </div>
        )}
      </div>

      {/* Appreciation Modal - Full Screen Overlay using Portal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {showAppreciationModal && selectedAppreciationActivity && appreciationModalData && (
            <div 
              className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4" 
              style={{ 
                position: 'fixed', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0,
                zIndex: 99999,
                margin: 0,
                padding: '1rem'
              }}
              onClick={(e) => {
                // Close modal when clicking outside
                if (e.target === e.currentTarget) {
                  setShowAppreciationModal(false);
                  setSelectedAppreciationActivity(null);
                  setAppreciationModalData(null);
                  setIsExpanded(false);
                  setActivityFile(null);
                }
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="bg-white rounded-3xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden relative"
                style={{ zIndex: 100000 }}
                onClick={(e) => e.stopPropagation()}
              >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 rounded-t-3xl relative z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Artwork Analysis
                </h3>
                <button
                  onClick={() => {
                  setShowAppreciationModal(false);
                  setSelectedAppreciationActivity(null);
                  setAppreciationModalData(null);
                  setIsExpanded(false);
                  setActivityFile(null);
                  }}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-lg z-20"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto relative z-10">
              <div className="space-y-6">
                {/* Artwork Info */}
                {appreciationModalData.artworkMeta && (
                  <div className="flex items-center space-x-4 p-4 bg-amber-50 rounded-xl border border-amber-200 mb-6">
                    {selectedAppreciationActivity.originalImageUrl && (
                      <img
                        src={selectedAppreciationActivity.originalImageUrl}
                        alt={appreciationModalData.artworkMeta.title || 'Artwork'}
                        className="w-16 h-16 object-cover rounded-lg border border-amber-200"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {appreciationModalData.artworkMeta.title || 'Untitled Artwork'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {appreciationModalData.artworkMeta.artist || 'Unknown Artist'}
                      </p>
                      {appreciationModalData.artworkMeta.movement && (
                        <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-full mt-1 inline-block">
                          {appreciationModalData.artworkMeta.movement}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Artwork Analysis - Hide when expanded or activity popup is open */}
                {!isExpanded && !isActivityPopupOpen && selectedAppreciationActivity.originalImageUrl && (
                  <div className="space-y-4 relative">
                    {/* Header */}
                    <h3 className="text-lg font-bold text-gray-900">Artwork Analysis</h3>

                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 border-4 border-amber-200 shadow-xl relative">
                      {/* Expand Button - Top Right Corner of Artwork Container - Small Size */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('[ArtHistoryCard] Expand button clicked');
                          setIsExpanded(true);
                        }}
                        className="absolute top-2 right-2 z-50 inline-flex items-center justify-center px-1.5 py-1 bg-white hover:bg-gray-50 rounded-lg shadow-sm transition-all duration-200 border border-gray-300 hover:border-amber-400"
                        title="Expand Artwork"
                        style={{ minWidth: '28px', minHeight: '28px' }}
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15.586 13H14a1 1 0 010-2h4z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <div className="flex items-center justify-center overflow-hidden" style={{ height: '600px', maxWidth: '100%' }}>
                        <div className="w-full h-full flex items-center justify-center">
                          <ArtAnalysisHotspots
                            imageUrl={selectedAppreciationActivity.originalImageUrl}
                            analysisText={(() => {
                              const appreciation = appreciationModalData.appreciation || [];
                              return appreciation.slice(0, 4).map(p => (typeof p === 'string' ? p : (p?.text || p?.label || '')));
                            })()}
                            title={appreciationModalData.artworkMeta?.title || 'Artwork'}
                            enableFullscreen={true}
                            layout="columnLeft"
                            concepts={appreciationModalData.concepts || []}
                            hideHotspots={isActivityPopupOpen}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Activity Section */}
                {appreciationModalData.activity && (
                  <div className="mt-6">
                    <ArtActivitySection 
                      activity={appreciationModalData.activity}
                      artworkData={{
                        imageUrl: selectedAppreciationActivity.originalImageUrl,
                        analysisText: (appreciationModalData.appreciation || []).map(p => (typeof p === 'string' ? p : (p?.text || p?.label || ''))).join('\n') || 'No analysis available',
                        title: appreciationModalData.artworkMeta?.title || 'Artwork',
                      }}
                      onPopupStateChange={setIsActivityPopupOpen}
                    />
                  </div>
                )}

                {/* Upload Activity Artwork Section */}
                <div className="mt-6 bg-white rounded-2xl shadow-lg p-6 border border-amber-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Upload Activity Artwork</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload your activity result
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setActivityFile(e.target.files[0])}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <button
                      onClick={() => {
                        if (!activityFile) {
                          alert('Please select a file to upload');
                          return;
                        }
                        setShowShareConfirmation(true);
                      }}
                      disabled={!activityFile || isUploading}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <FaUpload className="w-5 h-5" />
                      {isUploading ? 'Uploading...' : 'Upload Activity Artwork'}
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Share Confirmation Modal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {showShareConfirmation && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100001] p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Share to Co-Learning Space?</h3>
                <p className="text-gray-700 mb-6">
                  Would you like to share your artwork to the Artmosphere Co-Learning Space? This will allow other users to see your interpretation of this masterpiece.
                </p>
                <div className="flex gap-4">
                  <button
                    onClick={async () => {
                      setShowShareConfirmation(false);
                      await handleUploadActivity(false); // Upload as private
                    }}
                    disabled={isUploading}
                    className="flex-1 bg-gray-200 text-gray-800 font-semibold py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                  >
                    Upload Only
                  </button>
                  <button
                    onClick={async () => {
                      setShowShareConfirmation(false);
                      await handleUploadActivity(true); // Upload as public
                    }}
                    disabled={isUploading}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors disabled:opacity-50"
                  >
                    Share to Co-Learning
                  </button>
                </div>
                <button
                  onClick={() => setShowShareConfirmation(false)}
                  className="w-full mt-3 text-gray-600 hover:text-gray-800 text-sm"
                  disabled={isUploading}
                >
                  Cancel
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Expanded Artwork Overlay - Pop-out like ArtHistoryAppreciation.js - OUTSIDE MODAL */}
      {isExpanded && typeof document !== 'undefined' && selectedAppreciationActivity && appreciationModalData && createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 1000000 }}>
          <div className="relative w-[95vw] h-[85vh] max-w-6xl max-h-[85vh] bg-white rounded-2xl shadow-2xl border-4 border-amber-200 overflow-hidden" style={{ zIndex: 1000000 }}>
            {/* Close Button for Pop-out - Top Right - High z-index to stay above Key Concepts */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('[ArtHistoryCard] Close expanded view');
                setIsExpanded(false);
              }}
              className="absolute top-2 right-2 bg-white hover:bg-gray-50 rounded-lg shadow-lg transition-all duration-200 border border-gray-300 hover:border-amber-400"
              title="Close Expanded View"
              style={{ 
                minWidth: '28px', 
                minHeight: '28px',
                padding: '0.375rem',
                zIndex: 1000001,
                position: 'absolute'
              }}
            >
              <svg className="w-3 h-3 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            
            <div className="h-full flex items-center justify-center p-4">
              <div className="w-full h-full flex items-center justify-center">
                <ArtAnalysisHotspots
                  imageUrl={selectedAppreciationActivity.originalImageUrl}
                  analysisText={(() => {
                    const appreciation = appreciationModalData.appreciation || [];
                    return appreciation.slice(0, 4).map(p => (typeof p === 'string' ? p : (p?.text || p?.label || '')));
                  })()}
                  title={appreciationModalData.artworkMeta?.title || 'Artwork'}
                  enableFullscreen={false}
                  layout="columnLeft"
                  concepts={appreciationModalData.concepts || []}
                  hideHotspots={isActivityPopupOpen}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Success Popup Modal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {showSuccessPopup && uploadSuccessData && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100002] p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Success!</h3>
                  <p className="text-gray-700">{uploadSuccessData.message}</p>
                </div>
                <button
                  onClick={() => {
                    setShowSuccessPopup(false);
                    const wasShared = uploadSuccessData.shared;
                    setUploadSuccessData(null);
                    // Redirect based on share status
                    if (wasShared) {
                      // Redirect to Co-Learning Space (Art History tab) - correct route is /gallery/studio
                      navigate('/gallery/studio?tab=art-history');
                    } else {
                      // Redirect to Artist Wall
                      navigate('/artist-wall');
                    }
                  }}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-3 px-6 rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors"
                >
                  {uploadSuccessData.shared ? 'Go to Co-Learning Space' : 'Go to Artist Wall'}
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Comparison Modal - Full Screen Overlay using Portal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {showComparisonModal && selectedComparisonActivity && comparisonModalData && (
            <div 
              className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4" 
              style={{ 
                position: 'fixed', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0,
                zIndex: 99999,
                margin: 0,
                padding: '1rem'
              }}
              onClick={(e) => {
                // Close modal when clicking outside
                if (e.target === e.currentTarget) {
                  setShowComparisonModal(false);
                  setSelectedComparisonActivity(null);
                  setComparisonModalData(null);
                }
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="bg-white rounded-3xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-hidden relative"
                style={{ zIndex: 100000 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6 rounded-t-3xl relative z-10">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Art Comparison
                    </h3>
                    <button
                      onClick={() => {
                        setShowComparisonModal(false);
                        setSelectedComparisonActivity(null);
                        setComparisonModalData(null);
                        setActiveSimilarityIndex(null);
                        setActiveDifferenceIndex(null);
                        setIsInsightExpanded(false);
                      }}
                      className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-lg z-20"
                    >
                      <FaTimes className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto relative z-10">
                  <div className="space-y-6">
                    {/* Artworks Side by Side */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      {/* Artwork 1 */}
                      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm">
                        {comparisonModalData.artwork1?.imageUrl && (
                          <img 
                            src={comparisonModalData.artwork1.imageUrl} 
                            alt={comparisonModalData.artwork1.title || 'Artwork 1'} 
                            className="w-full object-contain" 
                            style={{ height: '400px' }} 
                          />
                        )}
                        <div className="p-4 bg-gray-50">
                          <div className="text-lg font-bold text-gray-800 mb-1">
                            {comparisonModalData.artwork1?.title || 'Artwork 1'}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            {comparisonModalData.artwork1?.artistName || comparisonModalData.artwork1?.artist || 'Unknown Artist'}
                          </div>
                          {comparisonModalData.artwork1?.artMovement && (
                            <div className="text-xs text-amber-600 font-medium bg-amber-100 px-2 py-1 rounded-full inline-block">
                              {comparisonModalData.artwork1.artMovement}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Artwork 2 */}
                      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm">
                        {comparisonModalData.artwork2?.imageUrl && (
                          <img 
                            src={comparisonModalData.artwork2.imageUrl} 
                            alt={comparisonModalData.artwork2.title || 'Artwork 2'} 
                            className="w-full object-contain" 
                            style={{ height: '400px' }} 
                          />
                        )}
                        <div className="p-4 bg-gray-50">
                          <div className="text-lg font-bold text-gray-800 mb-1">
                            {comparisonModalData.artwork2?.title || 'Artwork 2'}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            {comparisonModalData.artwork2?.artistName || comparisonModalData.artwork2?.artist || 'Unknown Artist'}
                          </div>
                          {comparisonModalData.artwork2?.artMovement && (
                            <div className="text-xs text-amber-600 font-medium bg-amber-100 px-2 py-1 rounded-full inline-block">
                              {comparisonModalData.artwork2.artMovement}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Comparison Results */}
                    {comparisonModalData.comparisonResult && (
                      <div className="space-y-4">
                        {/* Similarities */}
                        {comparisonModalData.comparisonResult.similarities && comparisonModalData.comparisonResult.similarities.length > 0 && (
                          <div className="bg-amber-50 rounded-xl p-4 border-2 border-amber-200">
                            <h4 className="text-lg font-bold text-amber-800 mb-3">Similarities</h4>
                            
                            {/* Numbered Buttons */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              {comparisonModalData.comparisonResult.similarities.map((_, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    setActiveSimilarityIndex(activeSimilarityIndex === index ? null : index);
                                  }}
                                  className={`w-10 h-10 rounded-full font-bold text-sm transition-all ${
                                    activeSimilarityIndex === index
                                      ? 'bg-amber-600 text-white shadow-lg scale-110'
                                      : 'bg-white text-amber-600 border-2 border-amber-300 hover:bg-amber-100'
                                  }`}
                                >
                                  {index + 1}
                                </button>
                              ))}
                            </div>
                            
                            {/* Content Display */}
                            {activeSimilarityIndex !== null && comparisonModalData.comparisonResult.similarities[activeSimilarityIndex] && (
                              <div className="bg-white rounded-lg p-4 border-2 border-amber-300 shadow-md">
                                <div className="flex items-start space-x-3">
                                  <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-white text-sm font-bold">{activeSimilarityIndex + 1}</span>
                                  </div>
                                  <p className="text-gray-700 leading-relaxed flex-1">
                                    {comparisonModalData.comparisonResult.similarities[activeSimilarityIndex]}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Differences */}
                        {comparisonModalData.comparisonResult.differences && comparisonModalData.comparisonResult.differences.length > 0 && (
                          <div className="bg-orange-50 rounded-xl p-4 border-2 border-orange-200">
                            <h4 className="text-lg font-bold text-orange-800 mb-3">Differences</h4>
                            
                            {/* Numbered Buttons */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              {comparisonModalData.comparisonResult.differences.map((_, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    setActiveDifferenceIndex(activeDifferenceIndex === index ? null : index);
                                  }}
                                  className={`w-10 h-10 rounded-full font-bold text-sm transition-all ${
                                    activeDifferenceIndex === index
                                      ? 'bg-orange-600 text-white shadow-lg scale-110'
                                      : 'bg-white text-orange-600 border-2 border-orange-300 hover:bg-orange-100'
                                  }`}
                                >
                                  {index + 1}
                                </button>
                              ))}
                            </div>
                            
                            {/* Content Display */}
                            {activeDifferenceIndex !== null && comparisonModalData.comparisonResult.differences[activeDifferenceIndex] && (
                              <div className="bg-white rounded-lg p-4 border-2 border-orange-300 shadow-md">
                                <div className="flex items-start space-x-3">
                                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-white text-sm font-bold">{activeDifferenceIndex + 1}</span>
                                  </div>
                                  <p className="text-gray-700 leading-relaxed flex-1">
                                    {comparisonModalData.comparisonResult.differences[activeDifferenceIndex]}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Insight */}
                        {comparisonModalData.comparisonResult.insight && (
                          <div className="bg-emerald-50 rounded-xl p-4 border-2 border-emerald-200">
                            <button
                              onClick={() => setIsInsightExpanded(!isInsightExpanded)}
                              className="w-full flex items-center justify-between text-left"
                            >
                              <h4 className="text-lg font-bold text-emerald-800">Learning Insight</h4>
                              <svg 
                                className={`w-5 h-5 text-emerald-600 transition-transform ${isInsightExpanded ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {isInsightExpanded && (
                              <div className="mt-3 bg-white rounded-lg p-4 border border-emerald-200">
                                <p className="text-gray-700 leading-relaxed">{comparisonModalData.comparisonResult.insight}</p>
                              </div>
                            )}
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default ArtHistoryCard;

